import { GenericLazyString } from '@eventbrite/i18n';
import { css } from 'emotion';
import React, { useEffect } from 'react';
import FloatingDialog, {
    IFloatingDialogMargins,
} from '../common/floating-dialog';
import UnitsAvailableLine from '../common/units-available-line';
import GeneralAdmisionLine from './general-admission-line';
import GeneralAdmisionManageSeatsLine from './general-admission-manage-seats-line';

interface IGeneralAdmissionFloatingDialog {
    arrowDialog?: 'top' | 'bottom';
    disableAddSeatsButton?: boolean;
    handleAddSeats: () => void;
    handleManageHolds: () => void;
    isDisplay?: boolean;
    margins: IFloatingDialogMargins;
    position: { x: number; y: number };
    positionType: 'arrow' | 'corner';
    removePanel?: () => void;
    section: {
        unitsAvailable: number;
        unitsTotal: number;
        hasHolds?: boolean;
        quantityHeldLabel: GenericLazyString;
    };
    setTicketQuantityAction: (_: any) => void;
    tickets: {
        ticketName: string;
        price: string | GenericLazyString;
        variantId: string;
        complimentaryOrder: boolean;
        quantity: string;
    }[];
    verticalOffset?: number;
    viewport: { width: number; height: number };
    cleanup: () => void;
}

const GeneralAdmissionFloatingDialog = ({
    arrowDialog,
    disableAddSeatsButton,
    handleAddSeats,
    handleManageHolds,
    isDisplay,
    margins,
    position,
    positionType,
    removePanel,
    section,
    setTicketQuantityAction,
    tickets = [],
    verticalOffset,
    viewport,
    cleanup,
}: IGeneralAdmissionFloatingDialog): React.ReactElement => {
    const { unitsAvailable, unitsTotal, hasHolds, quantityHeldLabel } = section;

    useEffect(() => {
        return () => cleanup && cleanup();
    }, [cleanup]);

    return (
        <FloatingDialog
            arrowDialog={arrowDialog}
            position={position}
            isDisplay={isDisplay}
            removePanel={removePanel}
            positionType={positionType}
            viewport={viewport}
            margins={margins}
            verticalOffset={verticalOffset}
        >
            <div>
                <div data-testid="units-available">
                    <UnitsAvailableLine
                        total={unitsTotal}
                        available={unitsAvailable}
                    />
                </div>
                <div
                    data-testid="tickets"
                    className={css({
                        overflowY: 'auto',
                        maxHeight: '232px',
                    })}
                >
                    {tickets.map(
                        ({
                            price = '',
                            ticketName = '',
                            variantId = '',
                            complimentaryOrder = false,
                            quantity = '',
                        }) => {
                            const content = (
                                <div
                                    key={ticketName}
                                    data-testid="general-admission-line"
                                >
                                    <GeneralAdmisionLine
                                        info={{
                                            ticketName,
                                            price,
                                            variantId,
                                            complimentaryOrder,
                                            quantity,
                                        }}
                                        setTicketQuantityAction={
                                            setTicketQuantityAction
                                        }
                                    />
                                </div>
                            );

                            return content;
                        },
                    )}
                </div>
                <div data-testid="manage-seats-line">
                    <GeneralAdmisionManageSeatsLine
                        disableAddSeatsButton={disableAddSeatsButton}
                        handleAddSeats={handleAddSeats}
                        handleManageHolds={handleManageHolds}
                        info={{
                            hasHolds,
                            quantityHeldLabel,
                        }}
                    />
                </div>
            </div>
        </FloatingDialog>
    );
};

export default GeneralAdmissionFloatingDialog;
